import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { skipToken } from '@reduxjs/toolkit/query';
import { chain, keyBy } from 'lodash';
import { SIcon } from '@chargebee/sting-react';
import { AsyncThemeWrapper } from '@bbkAdminComponents/async-theme-provider';
import { renderCategoryName } from '@bbkAdminComponents/experiences/offers/edit-offer-utils';
import {
  PreviewOverlay,
  PreviewWrapper,
} from '@bbkAdminComponents/experiences/previews';
import {
  getOfferPerformanceCtaRows,
  getOfferPerformanceReportRows,
} from '@bbkAdminComponents/offer-performance/offer-performance-report-rows';
import {
  PivotTableComponent,
  type PivotT,
} from '@bbkAdminComponents/pivot-table/pivot-table';
import { PivotTableTooltipContent } from '@bbkAdminComponents/pivot-table/pivot-table-content-tooltip';
import { useShowPlayDemoData } from '@bbkAdminComponents/play/play-context';
import {
  createOfferPerformanceCalculatedFields,
  DEFAULT_OFFER_PERF_GROUP_BY,
  getOfferPerformancePivotRowDefinitions,
  getOfferPerformancePivotValues,
  getOfferPerformanceVisibleColumns,
  OFFER_PERF_TABLE_NAME,
  resolveOfferPerformancePivotRows,
  type OfferPerformanceRow,
} from '@bbkAdminComponents/unified-plays/reporting/reports/cancel-page/cancel-page-offer-performance-export.config';
import { type CancelPlayReportProps } from '@bbkAdminComponents/unified-plays/reporting/reports/cancel-page/cancel-page-reports';
import { selectAppId } from '@bbkAdminRedux/app/selectors';
import {
  selectFromResultTyped,
  useAppDispatch,
} from '@bbkAdminRedux/redux_utils';
import {
  appPersistSlice,
  selectOfferPerfReportNav,
} from '@bbkAdminRedux/rtkq/app-persist/app-persist.slice';
import { BBKRequestContext } from '@bbkAdminRedux/rtkq/bbkApi.slice';
import { billingConfigSlice } from '@bbkAdminRedux/rtkq/billing-config.slice';
import {
  DEFAULT_BRAND_ID,
  useGetBrandOptions,
} from '@bbkAdminRedux/rtkq/brands.slice';
import { companyAppSettingsSlice } from '@bbkAdminRedux/rtkq/company-app-settings.slice';
import { useSelectDatePicker } from '@bbkAdminRedux/rtkq/datepicker-persist.slice';
import {
  useSelectAllFunnelsEntities,
  useSelectFunnelsLoading,
} from '@bbkAdminRedux/rtkq/funnels.slice';
import { useSelectBillingIntegration } from '@bbkAdminRedux/rtkq/integrations.slice';
import {
  offersAndLossAversionsSlice,
  useSelectAllOffersAndLossAversions,
} from '@bbkAdminRedux/rtkq/offers-and-lossAversions.slice';
import {
  reportsSlice,
  ReportType,
  type GetReportV2Args,
  type OfferPerformanceCtaReport,
  type OfferPerformanceV2Report,
  type ReportFilter,
} from '@bbkAdminRedux/rtkq/reports.slice';
import { isDefined } from '@bbkAdminRedux/rtkq/rtkq-utils';
import {
  rulesSlice,
  RuleType,
  useSelectAllRulesEntities,
} from '@bbkAdminRedux/rtkq/rules.slice';
import {
  isLossAversion,
  isModalCard,
  type RestLossAversionCardOrModalCard,
} from '@bbkAdminUtils/api-client/rest-cards-types';
import { FeatureFlagsE, useFeatureFlags } from '@bbkAdminUtils/feature-flags';
import { NumberKit } from '@bbkAdminUtils/number-kit';
import { usePlans } from '@bbkAdminUtils/plans';
import { useStrings } from '@bbkAdminUtils/strings';
import CardFactory from '@bbkFunnelComponents/card-factory';

const PlayOfferPerf: React.FC<CancelPlayReportProps> = ({ play }) => {
  const dispatch = useAppDispatch();
  const appKey = useSelector(selectAppId);
  const brandOptions = useGetBrandOptions({ appKey });
  const strings = useStrings();
  const brandId = play.brand_id ?? DEFAULT_BRAND_ID;

  const selectedBrand = useMemo(() => {
    return brandOptions.find((b) => b.value === brandId);
  }, [brandOptions, brandId]);

  const isDemoData = useShowPlayDemoData();

  const isNestedAudiencesEnabled = useFeatureFlags(
    FeatureFlagsE.ENABLE_NESTED_AUDIENCES_IN_PERF_REPORTS
  );

  const { isStarterFreePlan } = usePlans();
  const { data: appSettings } =
    companyAppSettingsSlice.endpoints.getAppSettings.useQuery({ appKey });
  const mrrActive = appSettings?.has_revenue;
  const exportBtnTooltip = {
    content: strings.UPGRADE_PLAN({
      description: strings.UPGRADE_TO_STARTER_PREMIUM_EXPORT,
    }),
    tooltipOptions: {
      placement: 'left',
    },
  };

  const {
    groupBy: initGroupByOpts,
    visibleColumns: initVisibleColumns,
    sortField: initSortField,
    sortOrderDesc: initSortOrderDesc,
  } = useSelector(selectOfferPerfReportNav(appKey));

  const billingIntegration = useSelectBillingIntegration({ appKey });

  const { data: billingConfig } =
    billingConfigSlice.endpoints.getBillingConfig.useQuery(
      billingIntegration ? { app_key: appKey } : skipToken
    );
  const isRepurchaseAttributionEnabled =
    billingConfig?.repurchase_based_attribution;

  const { startDate: start, endDate: end } = useSelectDatePicker();
  const reportType = ReportType.offer_performance_funnel_audiences_cta;
  const request = {
    report_type: reportType,
  };

  const commonArgs = {
    appKey,
    start,
    end,
    filters: [
      [
        {
          field: 'play',
          operator: 'equals' as const,
          value: play.id,
        } satisfies ReportFilter,
      ],
    ],
    context: isDemoData ? BBKRequestContext.offer_perf : undefined,
  };
  const offerPerfArgs: GetReportV2Args = {
    ...commonArgs,
    report_type_requests: [request],
  };
  const opts = {
    skip: !appKey || !start || !end,
    selectFromResult: selectFromResultTyped<
      OfferPerformanceV2Report | OfferPerformanceCtaReport
    >,
  };
  const {
    data: reportData,
    isFetching: isReportFetching,
    isError,
  } = reportsSlice.endpoints.getReportV2.useQuery(offerPerfArgs, opts);

  const ctaDataOffers = useMemo(() => {
    if (!reportData || isError) return [];
    return getOfferPerformanceCtaRows(reportData as OfferPerformanceCtaReport);
  }, [reportData, isError]);

  const offerPerformanceRows = useMemo(() => {
    if (!reportData || isError) return [];
    return getOfferPerformanceReportRows(
      reportData as OfferPerformanceCtaReport
    );
  }, [reportData, isError]);

  const offerCtaMap = chain(ctaDataOffers)
    .map((ctaPoint) => {
      const contentCta = ctaPoint.funnelAudContentCta.split(',').slice(-2);
      const offerId = contentCta[0] as string;
      const ctaId = contentCta[1] as string;
      return { offer_id: offerId, cta_id: ctaId };
    })
    .uniqBy((x) => [x.offer_id, x.cta_id].join('-'))
    .value();
  const { data: ctasText } =
    offersAndLossAversionsSlice.endpoints.getCtaTextByOfferIdCtaId.useQuery(
      { appKey, body: offerCtaMap },
      { skip: ctaDataOffers.length === 0 }
    );

  const ctasTextMap = useMemo(
    () => keyBy(ctasText?.result ?? [], (x) => x.cta_id),
    [ctasText]
  );

  const offersAndLossAversions = useSelectAllOffersAndLossAversions({
    appKey,
    context: BBKRequestContext.offer_perf,
  });
  const offersAndLAsByIdMap = useMemo(
    () => keyBy(offersAndLossAversions, (x) => x.id),
    [offersAndLossAversions]
  );
  const rulesArgs = {
    appKey,
    context: BBKRequestContext.offer_perf,
    includeAdditionalRules: [RuleType.SPLIT],
    brandId,
  };
  const { isFetching: isRulesFetching } =
    rulesSlice.endpoints.getRules.useQuery(rulesArgs);
  const rulesMap = useSelectAllRulesEntities(rulesArgs);

  const funnelsArgs = {
    appKey,
    context: BBKRequestContext.offer_perf,
    brandId,
  };
  const { isFetching: isFunnelsFetching } =
    useSelectFunnelsLoading(funnelsArgs);
  const funnelsMap = useSelectAllFunnelsEntities(funnelsArgs);

  const isFetching = isReportFetching || isRulesFetching || isFunnelsFetching;

  const calculatedFields = useMemo(
    () =>
      createOfferPerformanceCalculatedFields({
        rulesMap,
        funnelsMap,
        offersAndLAsByIdMap,
        ctasTextMap,
        selectedBrandLabel:
          typeof selectedBrand?.label === 'string'
            ? selectedBrand.label
            : undefined,
        isLossAversion,
        renderCategoryName,
      }),
    [
      rulesMap,
      funnelsMap,
      offersAndLAsByIdMap,
      ctasTextMap,
      selectedBrand?.label,
    ]
  );

  type PivotRow = PivotT.ComputedFields<
    OfferPerformanceRow,
    typeof calculatedFields
  >;
  type PivotValues = PivotT.PivotValue<PivotRow>[];

  const pivotRows = useMemo(() => {
    const allGroupByNames = getOfferPerformancePivotRowDefinitions(
      isNestedAudiencesEnabled
    ).map((row) => row.name);

    return resolveOfferPerformancePivotRows(
      allGroupByNames,
      isNestedAudiencesEnabled
    ).map((row) =>
      row.name === 'offer'
        ? {
            ...row,
            tooltip: (pivotRow: PivotRow) => {
              const offer = offersAndLAsByIdMap[pivotRow.offerId];
              if (!offer) return null;
              const card = offer.cards.find(
                (c) => c.name === offer.primary_card_name
              ) as RestLossAversionCardOrModalCard;
              return (
                <PreviewWrapper className="tw-zoom-40">
                  <PreviewOverlay />
                  <AsyncThemeWrapper modal={isModalCard(card)}>
                    <CardFactory card={card} isPreview offerManagementPage />
                  </AsyncThemeWrapper>
                </PreviewWrapper>
              );
            },
          }
        : row
    );
  }, [isNestedAudiencesEnabled, offersAndLAsByIdMap]);

  const pivotValues = useMemo((): PivotValues => {
    const basePivotValues = getOfferPerformancePivotValues({
      isDemoData,
      mrrActive,
      isRepurchaseAttributionEnabled,
    });

    return basePivotValues.map((pivotValue) => {
      switch (pivotValue.field) {
        case 'acceptRateCalc':
          return {
            ...pivotValue,
            tooltip: (row) => {
              const totalViews =
                row.totalViews === 0 && isDefined(row.ctaTotalViews)
                  ? row.ctaTotalViews
                  : row.totalViews;
              const totalViewsLabel =
                row.totalViews === 0 && isDefined(row.ctaTotalViews)
                  ? 'CTA Views'
                  : 'Offer Views';
              return (
                <PivotTableTooltipContent
                  formula={`Accepted Offers / ${totalViewsLabel}`}
                  calculation={
                    <>
                      {row.acceptedOfferCount ?? 0} / {totalViews} ={' '}
                      {NumberKit.formatPercent(row.acceptRateCalc)}
                    </>
                  }
                />
              );
            },
          };
        case 'saveRateCalc':
          return {
            ...pivotValue,
            tooltip: (row) => (
              <PivotTableTooltipContent
                formula="Saves / Total accepts"
                calculation={
                  <>
                    {row.saveCount} / {row.convRateViewed} ={' '}
                    {NumberKit.formatPercent(row.saveRateCalc)}
                  </>
                }
              />
            ),
          };
        case 'firstTimeRepurchaseRate':
          return {
            ...pivotValue,
            tooltip: (row) => (
              <PivotTableTooltipContent
                formula="First Repurchase Order / Offer accepts"
                calculation={
                  <>
                    {row.deflect_payment_sessions} / {row.acceptedOfferCount} ={' '}
                    {NumberKit.formatPercent(row.firstTimeRepurchaseRate)}
                  </>
                }
              />
            ),
          };
        default:
          return pivotValue;
      }
    });
  }, [isDemoData, mrrActive, isRepurchaseAttributionEnabled]);

  const visibleColumns = useMemo(
    () => getOfferPerformanceVisibleColumns(mrrActive, initVisibleColumns),
    [initVisibleColumns, mrrActive]
  );

  return (
    <div>
      <div className="tw-mb-3 tw-mt-2 tw-flex tw-items-center tw-gap-2">
        <div className="tw-flex tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-primary-100 tw-bg-primary-50 tw-p-2">
          <SIcon name="circle-x" size={18} className="tw-text-primary-600" />
        </div>
        <span className="tw-text-lg tw-font-semibold tw-text-neutral-900">
          Offer performance report
        </span>
      </div>
      <div className="tw-text-sm tw-font-light tw-text-neutral-600">
        Select an offer to show a breakdown by experience (cancel page).
      </div>
      <div className="tw-mb-6">
        <PivotTableComponent
          tableName={OFFER_PERF_TABLE_NAME}
          isFetching={isFetching}
          isExportDisabled={isStarterFreePlan || isDemoData}
          exportTooltip={isStarterFreePlan ? exportBtnTooltip : undefined}
          data={offerPerformanceRows}
          calculatedFields={calculatedFields}
          pivotValues={pivotValues}
          pivotRows={pivotRows}
          visibleColumns={visibleColumns}
          onVisibleColumnsChange={(value) => {
            dispatch(
              appPersistSlice.actions.saveOfferPerfReportNavState({
                appKey,
                visibleColumns: value,
              })
            );
          }}
          groupByOpts={initGroupByOpts ?? DEFAULT_OFFER_PERF_GROUP_BY}
          onGroupByOptsChange={(value) => {
            dispatch(
              appPersistSlice.actions.saveOfferPerfReportNavState({
                appKey,
                groupBy: value.filter(isDefined),
              })
            );
          }}
          sortField={initSortField}
          sortOrderDesc={initSortOrderDesc}
          onSortChange={(sortField, sortOrderDesc) => {
            dispatch(
              appPersistSlice.actions.saveOfferPerfReportNavState({
                appKey,
                sortField,
                sortOrderDesc,
              })
            );
          }}
        />
      </div>
    </div>
  );
};

export default PlayOfferPerf;
